import { ActionButton } from './ui/ActionButton';
import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

export type NmTabNavValue = string | number;

export interface NmTabNavItem<T extends NmTabNavValue> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

interface NmTabNavProps<T extends NmTabNavValue> {
  items: readonly NmTabNavItem<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  idBase?: string;
  panelId?: string;
  className?: string;
}

/** Shared glass tab navigation for switching between peer content panels. */
export function NmTabNav<T extends NmTabNavValue>({
  items,
  value,
  onChange,
  ariaLabel,
  idBase,
  panelId,
  className = '',
}: NmTabNavProps<T>) {
  const generatedId = useId().replace(/:/g, '');
  const baseId = idBase ?? `nm-tab-${generatedId}`;
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const selectAt = (index: number) => {
    const item = items[index];
    if (!item || item.disabled) return;
    onChange(item.value);
    buttonRefs.current[index]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    const enabledIndexes = items.flatMap((item, index) => (item.disabled ? [] : [index]));
    const enabledPosition = enabledIndexes.indexOf(currentIndex);
    if (enabledPosition < 0) return;

    let targetPosition: number | undefined;
    if (event.key === 'Home') targetPosition = 0;
    if (event.key === 'End') targetPosition = enabledIndexes.length - 1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      targetPosition = (enabledPosition + 1) % enabledIndexes.length;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      targetPosition = (enabledPosition - 1 + enabledIndexes.length) % enabledIndexes.length;
    }
    if (targetPosition === undefined) return;

    event.preventDefault();
    selectAt(enabledIndexes[targetPosition]);
  };

  return (
    <div className={`nm-tab-nav ui-action-tabs${className ? ` ${className}` : ''}`} role="tablist" aria-label={ariaLabel}>
      {items.map((item, index) => {
        const active = item.value === value;
        return (
          <ActionButton size="sm"
            key={item.value}
            ref={(node) => { buttonRefs.current[index] = node; }}
            id={`${baseId}-${String(item.value)}`}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={panelId}
            disabled={item.disabled}
            tabIndex={active ? 0 : -1}
            className={`nm-tab-nav__tab${active ? ' nm-tab-nav__tab--active' : ''}`}
            onClick={() => onChange(item.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {item.label}
          </ActionButton>
        );
      })}
    </div>
  );
}
